import { useState, useEffect } from "react";
import { TrendingUp, Mail, Send, ExternalLink, RefreshCw } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

interface CampaignStats {
  campaignId: string;
  campaignName: string;
  type: "welcome" | "value" | "pitch" | "reengagement";
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  sentDate: string;
  openRate: number;
  clickRate: number;
  bounceRate: number;
}

interface OverallStats {
  totalCampaigns: number;
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  averageOpenRate: number;
  averageClickRate: number;
  topPerformingCampaign: string;
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const CHART_TOOLTIP = { background: 'rgba(17,17,19,0.95)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px', color: '#fff' };
const INPUT = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/50 transition-all";

function CampaignAnalytics() {
  const [stats, setStats] = useState<CampaignStats[]>([]);
  const [overallStats, setOverallStats] = useState<OverallStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeRange, setTimeRange] = useState("30");
  const [campaignType, setCampaignType] = useState("all");

  useEffect(() => { fetchCampaignStats(); }, [timeRange, campaignType]);

  const fetchCampaignStats = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ days: timeRange, type: campaignType });
      const response = await fetch(`/api/articles/campaigns/analytics?${params}`);
      if (response.ok) {
        const data = await response.json();
        setStats(data.campaigns || []);
        setOverallStats(data.overall || null);
      }
    } catch (err) {
      console.error("Failed to fetch campaign stats:", err);
      setError("Failed to load campaign analytics");
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "welcome": return <Mail size={14} className="text-primary" />;
      case "value": return <TrendingUp size={14} className="text-green" />;
      case "pitch": return <Send size={14} className="text-orange" />;
      case "reengagement": return <ExternalLink size={14} className="text-purple" />;
      default: return <Mail size={14} className="text-white/40" />;
    }
  };

  const getTypeCls = (type: string) => {
    switch (type) {
      case "welcome": return "bg-primary/15 text-primary";
      case "value": return "bg-green/15 text-green";
      case "pitch": return "bg-orange/15 text-orange";
      case "reengagement": return "bg-purple/15 text-purple";
      default: return "bg-white/10 text-white/40";
    }
  };

  const chartData = stats.slice(0, 10).map((c) => ({
    name: c.campaignName.substring(0, 15) + (c.campaignName.length > 15 ? "…" : ""),
    sent: c.sent, opened: c.opened, clicked: c.clicked,
    openRate: c.openRate, clickRate: c.clickRate,
  }));

  const pieData = [
    { name: "Opened", value: stats.reduce((s, c) => s + c.opened, 0), color: "#34C759" },
    { name: "Clicked", value: stats.reduce((s, c) => s + c.clicked, 0), color: "#007AFF" },
    { name: "Bounced", value: stats.reduce((s, c) => s + c.bounced, 0), color: "#FF3B30" },
    { name: "Unsubscribed", value: stats.reduce((s, c) => s + c.unsubscribed, 0), color: "#FF9F0A" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">Campaign Analytics</h1>
        <div className="flex flex-wrap items-center gap-2">
          <select className={INPUT} value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <select className={INPUT} value={campaignType} onChange={(e) => setCampaignType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="welcome">Welcome</option>
            <option value="value">Value</option>
            <option value="pitch">Pitch</option>
            <option value="reengagement">Re-engagement</option>
          </select>
          <button onClick={fetchCampaignStats} disabled={loading}
            className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm">{error}</div>}

      {/* Overall Stats */}
      {overallStats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total Campaigns', value: overallStats.totalCampaigns, color: 'text-white' },
            { label: 'Total Sent', value: overallStats.totalSent.toLocaleString(), color: 'text-white' },
            { label: 'Total Opens', value: overallStats.totalOpens.toLocaleString(), color: 'text-green' },
            { label: 'Total Clicks', value: overallStats.totalClicks.toLocaleString(), color: 'text-primary' },
            { label: 'Avg Open Rate', value: `${(overallStats.averageOpenRate * 100).toFixed(1)}%`, color: 'text-purple' },
            { label: 'Avg Click Rate', value: `${(overallStats.averageClickRate * 100).toFixed(1)}%`, color: 'text-orange' },
          ].map((s, i) => (
            <div key={i} className={`${CARD} p-4`}>
              <p className="text-white/40 text-xs mb-1">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Performance Bar */}
        <div className={`${CARD} p-5 lg:col-span-2`}>
          <h2 className="text-sm font-semibold text-white mb-1">Campaign Performance (Last 10)</h2>
          <p className="text-white/40 text-xs mb-4">Sent vs opened vs clicked</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} />
              <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} />
              <Tooltip contentStyle={CHART_TOOLTIP} />
              <Legend />
              <Bar dataKey="sent" fill="rgba(255,255,255,0.15)" name="Sent" radius={[4, 4, 0, 0]} />
              <Bar dataKey="opened" fill="#34C759" name="Opened" radius={[4, 4, 0, 0]} />
              <Bar dataKey="clicked" fill="#007AFF" name="Clicked" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Engagement Pie */}
        <div className={`${CARD} p-5`}>
          <h2 className="text-sm font-semibold text-white mb-1">Overall Engagement</h2>
          <p className="text-white/40 text-xs mb-4">Breakdown by action</p>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pieData} cx="50%" cy="50%" outerRadius={90}
                labelLine={false}
                label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Rate Trends */}
      <div className={`${CARD} p-5`}>
        <h2 className="text-sm font-semibold text-white mb-1">Open & Click Rate Trends</h2>
        <p className="text-white/40 text-xs mb-4">Rate percentages across recent campaigns</p>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} />
            <YAxis domain={[0, 1]} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} />
            <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v: number | undefined) => v ? `${(v * 100).toFixed(1)}%` : '0%'} />
            <Legend />
            <Line type="monotone" dataKey="openRate" stroke="#34C759" strokeWidth={2} name="Open Rate" dot={{ fill: '#34C759', r: 3 }} />
            <Line type="monotone" dataKey="clickRate" stroke="#007AFF" strokeWidth={2} name="Click Rate" dot={{ fill: '#007AFF', r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Details Table */}
      <div className={CARD}>
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h2 className="text-sm font-semibold text-white">Campaign Details</h2>
        </div>
        {stats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-white/40 text-sm">No campaign data found for the selected period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['Campaign', 'Type', 'Sent', 'Opened', 'Clicked', 'Open Rate', 'Click Rate', 'Bounce Rate', 'Date'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.map((c) => (
                  <tr key={c.campaignId} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(c.type)}
                        <span className="text-white/80 text-xs max-w-[180px] truncate">{c.campaignName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getTypeCls(c.type)}`}>{c.type}</span>
                    </td>
                    <td className="px-4 py-3 text-white/60 text-xs">{c.sent.toLocaleString()}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{c.opened.toLocaleString()}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{c.clicked.toLocaleString()}</td>
                    <td className="px-4 py-3 text-green text-xs font-medium">{(c.openRate * 100).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-primary text-xs font-medium">{(c.clickRate * 100).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-red text-xs font-medium">{(c.bounceRate * 100).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{new Date(c.sentDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignAnalytics;
